import { type FC } from "react";

import { cx } from "@emotion/css";
import {
    Button,
    Card,
    Control,
    IconButton,
    Label,
    Option,
    Radio,
    RadioGroup,
    Status,
} from "@snapchat/snap-design-system";
import { AddIcon, TrashIcon } from "@snapchat/snap-design-system-icons";
import { Field, FieldArray, type FieldProps, type FormikErrors, type FormikTouched, useFormikContext } from "formik";

import { HttpRequestMethod } from "../../../../gql-generated/types";
import { Flexbox } from "../../../../shared/components";
import { FormikDropdownInput, FormikInput } from "../../../../shared/formik";
import { onlyCameraKitSelected } from "../../../utils/onlyCameraKitSelected";
import { controlStyle } from "../api-settings/ApiEdit.styles";
import {
    AddParameterButtonCta,
    DefaultValueLabel,
    DeleteMethod,
    DeleteParameterButtonCta,
    GetMethod,
    MethodLabel,
    NoLabel,
    ParameterLocationLabel,
    ParameterLocationTooltip,
    ParameterNameLabel,
    ParametersLabel,
    PathLabel,
    PathTooltip,
    PostMethod,
    PreviewLabel,
    ReferenceIdLabel,
    ReferenceIdTooltip,
    YesLabel,
} from "../messages.generated";
import {
    type RemoteApiFormValues,
    initialParameterValue,
    validParameterLocations,
} from "../schema/remoteApiSpecSchema";

import { card, cardError } from "./EndpointEdit.module.css";
import { buttonIcon, deleteStyle, subtitle } from "./endpoint.styles";
import { makeEndpointPrevew } from "./makeEndpointPreview";

interface CreateApiFormEndpointProps {
    key: string;
    index: number;
    onRemove: (index: number) => void;
}

// TODO(LOOK-160933): - move text to messages
export const EndpointEdit: FC<CreateApiFormEndpointProps> = ({ onRemove, key, index }) => {
    const { values, errors, touched } = useFormikContext<RemoteApiFormValues>();
    const { endpoints, targetPlatforms, host } = values;

    const currentMethod = endpoints[index]?.methods[0] ?? HttpRequestMethod.Get;

    const generalError =
        touched.endpoints?.some(Boolean) && typeof errors.endpoints?.[index] === "string"
            ? errors.endpoints[index]
            : null;

    const specificTouched = touched.endpoints?.[index];
    const specificError = errors.endpoints?.[index];

    const refIdError =
        specificTouched?.refId && (typeof specificError === "string" ? specificError : specificError?.refId);

    const pathError =
        specificTouched?.path && (typeof specificError === "string" ? specificError : specificError?.path);

    const isError = !!generalError || !!refIdError || !!pathError;

    return (
        <Card
            className={cx(card, isError && cardError)}
            title={`Endpoint ${index + 1}${endpoints[index]?.refId ? ` - ${endpoints[index]?.refId}` : ""}`}
            key={key}
            titleBorder={false}
            collapsible={true}
            collapsedByDefault={true}
            rightHeaderContent={
                <IconButton
                    icon={<TrashIcon />}
                    onClick={() => {
                        onRemove(index);
                    }}
                />
            }
            data-testid="api-create-endpoint-card"
        >
            <Field
                name={`endpoints.${index}.refId`}
                component={FormikInput}
                label={<ReferenceIdLabel />}
                tooltip={<ReferenceIdTooltip />}
                status={refIdError}
                statusType={refIdError && Status.ERROR}
                data-testid="api-create-endpoint-ref-id"
            />

            <Field
                name={`endpoints.${index}.path`}
                component={FormikInput}
                label={<PathLabel />}
                tooltip={<PathTooltip />}
                data-testid="api-create-endpoint-path"
                status={pathError}
                statusType={pathError && Status.ERROR}
            />

            <FieldArray name={`endpoints.${index}.methods`}>
                {({ replace }) => {
                    return (
                        <Control className={controlStyle} label={<MethodLabel />}>
                            <RadioGroup
                                onChange={(val) => {
                                    if (val !== currentMethod) {
                                        replace(0, val);
                                    }
                                }}
                                horizontal={true}
                                value={currentMethod}
                                disabled={!onlyCameraKitSelected(targetPlatforms)}
                            >
                                <Radio label={<GetMethod />} value={HttpRequestMethod.Get} />
                                <Radio label={<PostMethod />} value={HttpRequestMethod.Post} />
                                <Radio label={<DeleteMethod />} value={HttpRequestMethod.Delete} />
                            </RadioGroup>
                        </Control>
                    );
                }}
            </FieldArray>

            <Control className={controlStyle} label={<PreviewLabel />}>
                {makeEndpointPrevew(endpoints[index], host)}
            </Control>

            <Label className={subtitle}>
                <ParametersLabel />{" "}
            </Label>
            <FieldArray name={`endpoints.${index}.parameters`}>
                {({ push, remove }) => {
                    return (
                        <div>
                            {endpoints[index]?.parameters?.map((_parameter, parameterIndex) => {
                                const paramsTouched:
                                    | boolean
                                    | FormikTouched<{
                                          name: string;
                                          location: string;
                                          defaultValue: string;
                                      }> =
                                    typeof specificTouched?.parameters === "boolean"
                                        ? specificTouched.parameters
                                        : // Formik infers this as boolean | undefined but it should be FormikTouched
                                          (specificTouched?.parameters?.[parameterIndex] as unknown as
                                              | FormikTouched<{
                                                    name: string;
                                                    location: string;
                                                    defaultValue: string;
                                                }>
                                              | undefined) ?? {};

                                const paramError:
                                    | string
                                    | FormikErrors<{
                                          name: string;
                                          location: string;
                                          defaultValue: string;
                                      }> =
                                    (typeof specificError === "string"
                                        ? specificError
                                        : specificError?.parameters?.[parameterIndex]) ?? {};

                                const locationTouched =
                                    typeof paramsTouched === "boolean" ? paramsTouched : paramsTouched.location;

                                const nameTouched =
                                    typeof paramsTouched === "boolean" ? paramsTouched : paramsTouched.name;
                                const defaultValueTouched =
                                    typeof paramsTouched === "boolean" ? paramsTouched : paramsTouched.defaultValue;

                                const nameError =
                                    nameTouched &&
                                    (typeof paramError === "string" ? paramError : paramError.name ?? "");

                                const locationError =
                                    locationTouched &&
                                    (typeof paramError === "string" ? paramError : paramError.location ?? "");

                                const defaultValueError =
                                    defaultValueTouched &&
                                    (typeof paramError === "string" ? paramError : paramError.defaultValue ?? "");

                                return (
                                    <>
                                        <Field
                                            className={controlStyle}
                                            key={`parameter-${parameterIndex}`}
                                            name={`endpoints.${index}.parameters.${parameterIndex}.name`}
                                            component={FormikInput}
                                            label={<ParameterNameLabel />}
                                            status={nameError}
                                            statusType={nameError && Status.ERROR}
                                            data-testid={`api-create-endpoint-parameter-name-${parameterIndex}`}
                                        />

                                        <Field
                                            className={controlStyle}
                                            component={FormikDropdownInput}
                                            name={`endpoints.${index}.parameters.${parameterIndex}.location`}
                                            label={<ParameterLocationLabel />}
                                            showSearch={false}
                                            tooltip={<ParameterLocationTooltip />}
                                            status={locationError}
                                            statusType={locationError && Status.ERROR}
                                            data-testid={`api-create-endpoint-parameter-location-${parameterIndex}`}
                                        >
                                            {validParameterLocations.map((location) => (
                                                <Option key={location} value={location} />
                                            ))}
                                        </Field>

                                        <Field
                                            className={controlStyle}
                                            key={`parameter-${parameterIndex}`}
                                            name={`endpoints.${index}.parameters.${parameterIndex}.defaultValue`}
                                            component={FormikInput}
                                            label={<DefaultValueLabel />}
                                            status={defaultValueError}
                                            statusType={defaultValueError && Status.ERROR}
                                            data-testid={`api-create-endpoint-parameter-default-value-${parameterIndex}`}
                                        />

                                        <Field
                                            className={controlStyle}
                                            name={`endpoints.${index}.parameters.${parameterIndex}.required`}
                                        >
                                            {({ field, form }: FieldProps<boolean>) => {
                                                return (
                                                    <Control className={controlStyle} label="Required">
                                                        <RadioGroup
                                                            horizontal={true}
                                                            defaultValue={field.value}
                                                            onChange={(val: boolean) => {
                                                                if (val !== field.value) {
                                                                    void form.setFieldValue(
                                                                        `endpoints.${index}.parameters.${parameterIndex}.required`,
                                                                        val,
                                                                    );
                                                                }
                                                            }}
                                                        >
                                                            <Radio label={<YesLabel />} value={true} />
                                                            <Radio label={<NoLabel />} value={false} />
                                                        </RadioGroup>
                                                    </Control>
                                                );
                                            }}
                                        </Field>

                                        <Field
                                            className={controlStyle}
                                            name={`endpoints.${index}.parameters.${parameterIndex}.constant`}
                                        >
                                            {({ field, form }: FieldProps<boolean>) => {
                                                return (
                                                    <Control className={controlStyle} label="Constant">
                                                        <RadioGroup
                                                            horizontal={true}
                                                            defaultValue={field.value}
                                                            onChange={(val: boolean) => {
                                                                if (val !== field.value) {
                                                                    void form.setFieldValue(
                                                                        `endpoints.${index}.parameters.${parameterIndex}.constant`,
                                                                        val,
                                                                    );
                                                                }
                                                            }}
                                                        >
                                                            <Radio label={<YesLabel />} value={true} />
                                                            <Radio label={<NoLabel />} value={false} />
                                                        </RadioGroup>
                                                    </Control>
                                                );
                                            }}
                                        </Field>

                                        <button className={deleteStyle} onClick={() => remove(parameterIndex)}>
                                            <DeleteParameterButtonCta />
                                        </button>
                                    </>
                                );
                            })}

                            <Flexbox justifyContent="center">
                                <Button
                                    onClick={() => {
                                        push(initialParameterValue);
                                    }}
                                >
                                    <AddIcon className={buttonIcon} />
                                    <AddParameterButtonCta />
                                </Button>
                            </Flexbox>
                        </div>
                    );
                }}
            </FieldArray>
        </Card>
    );
};
